import { api } from '@/lib/api'
import { ChatSession, ChatMessage } from '@/types'

export interface CreateChatSessionDto {
  studentId: string
  type?: 'career_guidance' | 'college_prep' | 'academic_support' | 'personal_counseling'
  notes?: string
}

export interface SendMessageDto {
  sessionId: string
  content: string
  messageType?: 'text' | 'file' | 'image'
  fileName?: string
  fileUrl?: string
}

export interface ChatSessionWithMessages extends ChatSession {
  messages: ChatMessage[]
  unreadCount: number
}

export interface StudentInfo {
  id: string
  name: string
  email?: string
  profilePicture?: string
  phoneNumber?: string
}

export interface LatestMessageInfo {
  id: string
  message: string
  messageType: string
  sentAt: string
  senderId: string
  isRead: boolean
}

export interface ChatSessionWithLatest {
  session: ChatSession
  student: StudentInfo
  latestMessage?: LatestMessageInfo
  unreadCount: number
  lastActivityAt: string
}

export interface ChatSessionsWithLatestResponse {
  sessions: ChatSessionWithLatest[]
  total: number
}

// Get all chat sessions for counselor
export const getChatSessions = async (params?: {
  status?: 'active' | 'completed' | 'cancelled'
  page?: number
  limit?: number
}): Promise<{ sessions: ChatSession[], total: number }> => {
  return api.get('/chat/sessions', params)
}

// Get specific chat session with messages
export const getChatSession = async (sessionId: string): Promise<ChatSessionWithMessages> => {
  return api.get<ChatSessionWithMessages>(`/chat/sessions/${sessionId}`)
}

// Get messages for a chat session
export const getChatMessages = async (
  sessionId: string,
  params?: {
    page?: number
    limit?: number
    lastMessageId?: string
  }
): Promise<{ messages: ChatMessage[], hasMore: boolean }> => {
  return api.get(`/chat/sessions/${sessionId}/messages`, params)
}

// Create a new chat session
export const createChatSession = async (data: CreateChatSessionDto): Promise<ChatSession> => {
  return api.post<ChatSession>('/chat/sessions', data)
}

// Send a message
export const sendMessage = async (data: SendMessageDto): Promise<ChatMessage> => {
  return api.post<ChatMessage>('/chat/messages', data)
}

// End a chat session
export const endChatSession = async (
  sessionId: string,
  data?: {
    summary?: string
    notes?: string
    rating?: number
    followUpRequired?: boolean
  }
): Promise<ChatSession> => {
  return api.patch<ChatSession>(`/chat/sessions/${sessionId}/end`, data)
}

// Mark messages as read
export const markMessagesAsRead = async (sessionId: string): Promise<void> => {
  return api.patch<void>(`/chat/sessions/${sessionId}/read`)
}

// Get chat sessions with latest message for counselor
export const getChatSessionsWithLatestMessage = async (): Promise<ChatSessionsWithLatestResponse> => {
  return api.get<ChatSessionsWithLatestResponse>('/chat/sessions/with-latest-message')
}